import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Owner } from '@/types';
import { useCreateOwner, useUpdateOwner } from '@/hooks/useOwners';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';

const ownerSchema = z.object({
  first_name: z.string().min(1, 'El nombre es requerido'),
  last_name: z.string().min(1, 'El apellido es requerido'),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  phone: z.string().optional(),
  phone_secondary: z.string().optional(),
  address: z.string().optional(),
  notes: z.string().optional(),
});

type OwnerFormData = z.infer<typeof ownerSchema>;

interface OwnerFormProps {
  owner?: Owner | null;
  onSuccess: () => void;
}

export default function OwnerForm({ owner, onSuccess }: OwnerFormProps) {
  const createOwner = useCreateOwner();
  const updateOwner = useUpdateOwner();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OwnerFormData>({
    resolver: zodResolver(ownerSchema),
    defaultValues: owner
      ? {
          first_name: owner.first_name,
          last_name: owner.last_name,
          email: owner.email || '',
          phone: owner.phone || '',
          phone_secondary: owner.phone_secondary || '',
          address: owner.address || '',
          notes: owner.notes || '',
        }
      : {},
  });

  const onSubmit = async (data: OwnerFormData) => {
    try {
      // Clean empty strings
      const cleanData = {
        ...data,
        email: data.email || undefined,
        phone: data.phone || undefined,
        phone_secondary: data.phone_secondary || undefined,
        address: data.address || undefined,
        notes: data.notes || undefined,
      };

      if (owner) {
        await updateOwner.mutateAsync({ id: owner.id, data: cleanData });
      } else {
        await createOwner.mutateAsync(cleanData);
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving owner:', error);
    }
  };

  const isLoading = createOwner.isPending || updateOwner.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          id="first_name"
          label="Nombre *"
          {...register('first_name')}
          error={errors.first_name?.message}
        />
        <Input
          id="last_name"
          label="Apellido *"
          {...register('last_name')}
          error={errors.last_name?.message}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          id="phone"
          label="Teléfono principal"
          {...register('phone')}
          error={errors.phone?.message}
        />
        <Input
          id="email"
          type="email"
          label="Email"
          {...register('email')}
          error={errors.email?.message}
        />
      </div>

      <Input
        id="phone_secondary"
        label="Teléfono secundario"
        {...register('phone_secondary')}
        error={errors.phone_secondary?.message}
      />

      <Input
        id="address"
        label="Dirección"
        {...register('address')}
        error={errors.address?.message}
      />

      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-taupe-700 mb-1.5">
          Notas
        </label>
        <textarea
          id="notes"
          {...register('notes')}
          rows={3}
          className="w-full px-4 py-3 border border-cream-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all text-taupe-800 placeholder-taupe-400"
          placeholder="Observaciones adicionales..."
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="submit" loading={isLoading}>
          {owner ? 'Guardar Cambios' : 'Crear Propietario'}
        </Button>
      </div>
    </form>
  );
}
